import { useEditor, EditorContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import Placeholder from '@tiptap/extension-placeholder'
import { useEffect, useRef } from 'react'
import {
  normalizePastedRichTextHtml,
  normalizePastedRichTextText,
} from 'src/common/richTextEditor/tiptapPasteUtils'

/**
 * TipTap Editor Component for Patient History Notes
 * Modern replacement for ReactQuill - eliminates findDOMNode warnings
 */
const PatientHistoryTipTapEditor = ({
  value = '',
  onChange,
  placeholder = 'Enter Content',
  readOnly = false,
  containerDivStyles = {},
  scrollableContentArea = false,
  maxHeightForScroll = 320,
}) => {
  // Refs so the stable onUpdate closure always has the latest values without recreating the editor
  const _readOnlyRef = useRef(readOnly)
  _readOnlyRef.current = readOnly
  const _onChangeRef = useRef(onChange)
  _onChangeRef.current = onChange
  const _onChangeTimerRef = useRef(null)
  // Track the last HTML value we sent to the parent via onChange.
  // The sync useEffect must skip calling setContent for this value, otherwise
  // the editor reverts the user's latest typed content to the debounced snapshot.
  const _lastSentValueRef = useRef(null)

  useEffect(() => () => clearTimeout(_onChangeTimerRef.current), [])

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
        blockquote: {
          HTMLAttributes: {
            class: 'tiptap-blockquote',
          },
        },
        link: false,
        underline: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'tiptap-link',
        },
      }),
      Image.configure({
        inline: false,
        allowBase64: false,
        HTMLAttributes: {
          class: 'tiptap-image',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: value || '',
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      if (!_readOnlyRef.current && _onChangeRef.current) {
        clearTimeout(_onChangeTimerRef.current)
        _onChangeTimerRef.current = setTimeout(() => {
          // Capture the CURRENT editor state at debounce fire time (not at keystroke time)
          const html = editor.getHTML()
          _lastSentValueRef.current = html
          _onChangeRef.current(html)
        }, 300)
      }
    },
    editorProps: {
      // Normalize clipboard noise, but keep ProseMirror's native paste parsing and selection handling.
      transformPastedHTML: normalizePastedRichTextHtml,
      transformPastedText: normalizePastedRichTextText,
      attributes: {
        class: readOnly ? 'patient-history-tiptap-editor read-only' : 'patient-history-tiptap-editor',
      },
    },
  })

  // Update editor content when value prop changes from OUTSIDE (not an echo of our own onChange).
  // Without this guard, a debounced onChange → parent state update → value prop update cycle
  // would call setContent with a 300ms-old snapshot, reverting whatever the user typed since.
  useEffect(() => {
    if (editor && value !== undefined) {
      // Skip if this value is the one we just sent up — it's our own echo, not an external change
      if (value === _lastSentValueRef.current) return
      const currentContent = editor.getHTML()
      if (currentContent !== value) {
        editor.commands.setContent(value || '', false)
      }
    }
  }, [editor, value])

  // Update editable state when readOnly changes
  useEffect(() => {
    if (editor) {
      editor.setEditable(!readOnly)
    }
  }, [editor, readOnly])

  if (!editor) {
    return null
  }

  return (
    <div
      className={scrollableContentArea ? 'patient-history-tiptap-scrollable-container' : undefined}
      style={{
        background: 'white',
        padding: 0,
        border: 'none',
        ...(scrollableContentArea
          ? {
              display: 'flex',
              flexDirection: 'column',
              maxHeight: maxHeightForScroll,
              overflow: 'hidden',
              minHeight: 0,
            }
          : {}),
        ...containerDivStyles,
      }}
    >
      {!readOnly && (
        <div className="patient-history-tiptap-toolbar" style={scrollableContentArea ? { flexShrink: 0 } : undefined}>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            className={editor.isActive('heading', { level: 1 }) ? 'is-active' : ''}
            title="Heading 1"
          >
            H1
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            className={editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}
            title="Heading 2"
          >
            H2
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            className={editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}
            title="Heading 3"
          >
            H3
          </button>
          <div className="toolbar-divider" />
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBold().run()}
            className={editor.isActive('bold') ? 'is-active' : ''}
            title="Bold"
          >
            <strong>B</strong>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            className={editor.isActive('italic') ? 'is-active' : ''}
            title="Italic"
          >
            <em>I</em>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            className={editor.isActive('underline') ? 'is-active' : ''}
            title="Underline"
          >
            <u>U</u>
          </button>
          <div className="toolbar-divider" />
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={editor.isActive('bulletList') ? 'is-active' : ''}
            title="Bullet List"
          >
            •
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={editor.isActive('orderedList') ? 'is-active' : ''}
            title="Ordered List"
          >
            1.
          </button>
          <button
            type="button"
            onClick={() => {
              const url = window.prompt('Enter URL:')
              if (url) {
                editor.chain().focus().setLink({ href: url }).run()
              }
            }}
            className={editor.isActive('link') ? 'is-active' : ''}
            title="Link"
          >
            🔗
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            className={editor.isActive('blockquote') ? 'is-active' : ''}
            title="Blockquote"
          >
            "
          </button>
          <div className="toolbar-divider" />
          <button
            type="button"
            onClick={() => editor.chain().focus().clearNodes().unsetAllMarks().run()}
            title="Clear Formatting"
          >
            Clear
          </button>
        </div>
      )}

      {scrollableContentArea ? (
        <div
          className="patient-history-tiptap-content-scroll"
          style={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            overflowWrap: 'break-word',
          }}
        >
          <EditorContent editor={editor} />
        </div>
      ) : (
        <EditorContent editor={editor} />
      )}

      <style dangerouslySetInnerHTML={{
        __html: [
          '.patient-history-tiptap-toolbar {',
          '  display: flex;',
          '  align-items: center;',
          '  gap: 4px;',
          '  padding: 8px 12px;',
          '  background: #f8f9fa;',
          '  border-bottom: 1px solid #e0e0e0;',
          '  flex-wrap: wrap;',
          '  overflow-x: auto;',
          '  -webkit-overflow-scrolling: touch;',
          '}',
          '@media (max-width: 600px) {',
          '  .patient-history-tiptap-toolbar {',
          '    padding: 6px 8px;',
          '    gap: 3px;',
          '  }',
          '}',
          '.patient-history-tiptap-toolbar button {',
          '  display: flex;',
          '  align-items: center;',
          '  justify-content: center;',
          '  padding: 4px 8px;',
          '  border: none;',
          '  background: transparent;',
          '  border-radius: 4px;',
          '  cursor: pointer;',
          '  font-size: 13px;',
          '  color: #64748b;',
          '  transition: all 0.2s;',
          '  min-width: 28px;',
          '  height: 28px;',
          '  touch-action: manipulation;',
          '  -webkit-tap-highlight-color: transparent;',
          '}',
          '@media (max-width: 600px) {',
          '  .patient-history-tiptap-toolbar button {',
          '    min-width: 32px;',
          '    height: 32px;',
          '    padding: 6px;',
          '    font-size: 12px;',
          '  }',
          '}',
          '.patient-history-tiptap-toolbar button:hover,',
          '.patient-history-tiptap-toolbar button:active {',
          '  background: rgba(99, 102, 241, 0.1);',
          '  color: #4338ca;',
          '}',
          '.patient-history-tiptap-toolbar button.is-active {',
          '  background: rgba(99, 102, 241, 0.15);',
          '  color: #4338ca;',
          '  font-weight: 600;',
          '}',
          '.toolbar-divider {',
          '  width: 1px;',
          '  height: 20px;',
          '  background: #e5e7eb;',
          '  margin: 0 4px;',
          '  flex-shrink: 0;',
          '}',
          '@media (max-width: 600px) {',
          '  .toolbar-divider {',
          '    height: 24px;',
          '    margin: 0 2px;',
          '  }',
          '}',
          '.patient-history-tiptap-editor {',
          '  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu",',
          '    "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif;',
          '  font-size: 14px;',
          '  line-height: 1.6;',
          '  color: #1f2937;',
          '  padding: 12px 16px;',
          '  min-height: 120px;',
          '  outline: none;',
          '  word-wrap: break-word;',
          '  overflow-wrap: break-word;',
          '}',
          '@media (max-width: 600px) {',
          '  .patient-history-tiptap-editor {',
          '    font-size: 16px;',
          '    padding: 10px 12px;',
          '    min-height: 100px;',
          '    line-height: 1.5;',
          '  }',
          '}',
          '.patient-history-tiptap-editor.read-only {',
          '  background: #fafafa;',
          '  cursor: default;',
          '}',
          '.patient-history-tiptap-editor p {',
          '  margin: 0.5em 0;',
          '}',
          '@media (max-width: 600px) {',
          '  .patient-history-tiptap-editor p {',
          '    margin: 0.4em 0;',
          '  }',
          '}',
          '.patient-history-tiptap-editor p.is-editor-empty:first-child::before {',
          '  content: attr(data-placeholder);',
          '  float: left;',
          '  color: rgba(0, 0, 0, 0.4);',
          '  pointer-events: none;',
          '  height: 0;',
          '}',
          '.patient-history-tiptap-editor h1 {',
          '  font-size: 24px;',
          '  font-weight: 700;',
          '  margin-top: 0.5em;',
          '  margin-bottom: 0.3em;',
          '}',
          '@media (max-width: 600px) {',
          '  .patient-history-tiptap-editor h1 {',
          '    font-size: 20px;',
          '  }',
          '}',
          '.patient-history-tiptap-editor h2 {',
          '  font-size: 20px;',
          '  font-weight: 700;',
          '  margin-top: 0.5em;',
          '  margin-bottom: 0.3em;',
          '}',
          '@media (max-width: 600px) {',
          '  .patient-history-tiptap-editor h2 {',
          '    font-size: 18px;',
          '  }',
          '}',
          '.patient-history-tiptap-editor h3 {',
          '  font-size: 18px;',
          '  font-weight: 600;',
          '  margin-top: 0.5em;',
          '  margin-bottom: 0.3em;',
          '}',
          '@media (max-width: 600px) {',
          '  .patient-history-tiptap-editor h3 {',
          '    font-size: 16px;',
          '  }',
          '}',
          '.patient-history-tiptap-editor .tiptap-link {',
          '  color: #1a8917;',
          '  text-decoration: none;',
          '  word-break: break-all;',
          '}',
          '.patient-history-tiptap-editor .tiptap-link:hover {',
          '  text-decoration: underline;',
          '}',
          '.patient-history-tiptap-editor .tiptap-blockquote {',
          '  border-left: 3px solid rgba(0, 0, 0, 0.84);',
          '  padding-left: 16px;',
          '  margin: 1em 0;',
          '  font-style: italic;',
          '  color: rgba(0, 0, 0, 0.68);',
          '}',
          '@media (max-width: 600px) {',
          '  .patient-history-tiptap-editor .tiptap-blockquote {',
          '    padding-left: 12px;',
          '    margin: 0.8em 0;',
          '  }',
          '}',
          '.patient-history-tiptap-editor .tiptap-image {',
          '  max-width: 100%;',
          '  height: auto;',
          '  display: block;',
          '  margin: 1em auto;',
          '  border-radius: 4px;',
          '}',
          '.patient-history-tiptap-editor ul,',
          '.patient-history-tiptap-editor ol {',
          '  padding-left: 1.5em;',
          '  margin: 0.5em 0;',
          '}',
          '@media (max-width: 600px) {',
          '  .patient-history-tiptap-editor ul,',
          '  .patient-history-tiptap-editor ol {',
          '    padding-left: 1.2em;',
          '    margin: 0.4em 0;',
          '  }',
          '}',
          '.patient-history-tiptap-editor li {',
          '  margin: 0.25em 0;',
          '}',
          '@media (max-width: 600px) {',
          '  .patient-history-tiptap-editor li {',
          '    margin: 0.2em 0;',
          '  }',
          '}',
        ].join('\n')
      }} />
    </div>
  )
}

export default PatientHistoryTipTapEditor
